import {
  CalendarItemType,
  CreateMealInput,
  Meal as MealGrapqhl,
  MealRecipe,
  UpdateMealInput,
} from '@akin-home/amplify';
import { excludeNullOrUndefined } from '@akin-home/arrays';
import { handleImageFieldsOnPost } from '@akin-home/images/frontend';
import { Meal, createMealModel, replaceMealRecipe } from '@akin-home/meals';
import { Recipe, createRecipe } from '@akin-home/recipes';
import { getId } from '@akin-home/records';
import {
  computeUntilDate,
  createRRule,
  serializeRRule,
} from '@akin-home/recurrence';
import { JSONParseSafe, JSONStringifySafe } from '@akin-home/utils/json';
import { createMealRecipeLink, deleteMealRecipeLink, updateMealMutation } from '../api/mutations';

export * from './saveMealPreferencesToStateVariables';

export const mealGraphqlToModel = (item: MealGrapqhl): Meal =>
  createMealModel({
    id: item.id,
    habitatID: item.habitatID,
    mealType: item.mealType,
    title: item.title,
    emoji: item.emoji || '',
    start: item.start ? new Date(item.start) : new Date(),
    end: item.end ? new Date(item.end) : undefined,
    location: item.location || '',
    notes: item.notes || '',
    participantIDS: item.participantIDS as string[] | undefined,
    recurrenceUntil: item.recurrenceUntil
      ? new Date(item.recurrenceUntil)
      : undefined,
    recurrence: item.recurrence && createRRule(item.recurrence),
    prepNotes: (item.prepNotes || []) as any[],
    ingredients: (item.ingredients || []) as any[],
    publicImageUrl: item.imageURL || undefined,
    imageUrl: item.imageURL || undefined,
    image: item.image || undefined,
    imageCache: item.image || undefined,
    recurrenceExcludeDates: JSONParseSafe(item.recurrenceExcludeDates, {}),
    recurrenceInstanceData: JSONParseSafe(item.recurrenceInstanceMap, {}),
    eaten: item.eaten || false,
    recipeM2m: (item.recipes?.items || []) as MealRecipe[],
    recipes: excludeNullOrUndefined<MealRecipe>(item.recipes?.items as any).map(
      (m) => createRecipe(m.recipe as any)
    ),
    updatedAt: item.updatedAt,
    createdAt: item.createdAt,
  });

export const mealModelToCreateGraphql = async (
  m: Meal
): Promise<CreateMealInput> => {
  m.recipes.forEach(
    async (recipe) => await createMealRecipeLink(m.id, recipe.id)
  );

  const meal = await handleImageFieldsOnPost(m);

  return {
    ...serialize(meal),
    type: CalendarItemType.Meal,
    mealType: meal.mealType,
    location: meal.location,
    end: meal.end.toISOString(),
    participantIDS: meal.participantIDS,
    ingredients: meal.ingredients || [],
    prepNotes: meal.prepNotes || [],
    eaten: meal.eaten,
    imageURL: meal.imageUrl || meal.publicImageUrl || null,
  };
};

export const mealModelToUpdateGraphql = async (
  m: Meal
): Promise<UpdateMealInput> => {
  const recipes = m.recipes;
  const recipeM2m = m.recipeM2m;
  const m2mDiff = getRecipeM2mDiff(recipes, recipeM2m);
  m2mDiff.add.forEach(
    async (recipeID) => await createMealRecipeLink(m.id, recipeID)
  );
  m2mDiff.delete.forEach(async (linkID) => await deleteMealRecipeLink(linkID));

  const meal = await handleImageFieldsOnPost(m);

  return {
    ...serialize(meal),
    type: CalendarItemType.Meal,
    mealType: meal.mealType,
    location: meal.location,
    end: meal.end.toISOString(),
    participantIDS: meal.participantIDS,
    ingredients: meal.ingredients || [],
    prepNotes: meal.prepNotes || [],
    eaten: meal.eaten,
    imageURL: meal.imageUrl || meal.publicImageUrl || null,
  };
};

const serialize = (item: Meal) => ({
  id: item.id,
  title: item.title,
  habitatID: item.habitatID,
  start: item.start.toISOString(),
  notes: item.notes,
  emoji: item.emoji,
  recurrence: item.recurrence && serializeRRule(item.recurrence),
  recurrenceUntil: item.recurrence
    ? computeUntilDate(item.recurrence).toISOString()
    : null,
  recurrenceExcludeDates: JSONStringifySafe(item.recurrenceExcludeDates, null),
  recurrenceInstanceMap: JSONStringifySafe(item.recurrenceInstanceData, null),
  imageID: item.image?.id || null,
});

export const getRecipeM2mDiff = (
  recipes: Recipe[],
  recipeM2m: MealRecipe[]
): { add: string[]; delete: string[] } => {
  const m2mRecipeIds = recipeM2m.map((m2m) => m2m.recipeID) as string[];
  const recipeIds = recipes.map(getId);

  return {
    add: recipeIds.filter((recipeID) => !m2mRecipeIds.includes(recipeID)),
    delete: recipeM2m
      .filter((m2m) => !recipeIds.includes(m2m.recipeID as string))
      .map(getId),
  };
};


export const swapMealRecipeMutation = async (data: {
  meal: Meal;
  recipe: Recipe;
}): Promise<Meal> => {
  const { meal, recipe } = data;

  // Update the meal data with the given recipe
  const updatedMeal = replaceMealRecipe(recipe, meal);

  // Update the meal on the server
  const res = await updateMealMutation({
    input: await mealModelToUpdateGraphql(updatedMeal),
  });

  // Return Meal Data
  return mealGraphqlToModel(res.data.updateMeal as MealGrapqhl);
};

